import React, { useEffect } from "react";
import { Route, RouteProps } from "react-router-dom";

import { useNavigate } from "hooks";
import { NavigationSettings } from "hooks/shared-hooks/use-navigate";
import { useDealUserRoleSelect, useUserRoleSelector } from "store";
import { IRoleName, RoutesKeys } from "store/types";

type RoleType = "account" | "deal";
interface Props extends Omit<RouteProps, "component"> {
  children: React.ReactNode;
  availableFor: IRoleName[];
  // redirectTo: RoutesKeys;
  redirectTo: NavigationSettings<RoutesKeys>;
  whichRole?: RoleType;
  enabled?: boolean;
}

export const ProtectedRoute: React.FC<Props> = ({
  children,
  availableFor,
  redirectTo,
  whichRole = "account",
  enabled = true,
  ...rest
}) => {
  const { name: accountRole } = useUserRoleSelector() || {};
  const dealRole = useDealUserRoleSelect();
  const navigate = useNavigate();

  const roles: Record<RoleType, IRoleName | undefined | null> = {
    account: accountRole,
    deal: dealRole
  };
  const selectedRole = roles[whichRole];

  // Computed on every render (not cached in state via a one-shot effect) so the guard
  // stays correct both when the role/flag loads asynchronously and when <Switch> reuses
  // this instance for a different route — in that case only the props change, no remount.
  const isResolved = Boolean(selectedRole);
  const isAllowed = isResolved && enabled && availableFor.includes(selectedRole!);

  useEffect(() => {
    if (isResolved && !isAllowed) {
      navigate(redirectTo);
    }
    // redirectTo/navigate excluded on purpose: redirectTo is a fresh ref each render and
    // we only need to fire the redirect when access for the current route is denied.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResolved, isAllowed, redirectTo.route]);

  if (!isAllowed) return null;

  return <Route {...rest}>{children}</Route>;
};
